import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMyBotsQuery } from '../../hooks/queries';
import { useAuth } from '../../contexts/AuthContext';

// The Assistants the app used to create on its own are named (and described) in the
// viewer's language until someone renames them (name_is_default flips to false).
export const botDisplayName = (bot, t) =>
  (bot?.name_is_default ? t('bots.default_bot_name') : bot?.name) || '';

/** The team a team bot belongs to (personal bots: none), so same-named bots can be told apart. */
export const botTeamName = (bot) => (bot?.owner_type === 'team' ? bot?.scope?.name || null : null);

/**
 * A bot's name wherever it is shown as text: the team after it when another of the
 * viewer's bots reads the same ("Tester · BusyBee.Day Team"), so a person always knows
 * which bot is which. Rows with room for it show the team always (BotName).
 */
export const botLabel = (bot, t, all = []) => {
  if (!bot) return '';
  const full = (bot.scope ? null : all.find((b) => b.id === bot.id)) || bot;  // the team is on the viewer's bots
  const name = botDisplayName(full, t);
  const team = botTeamName(full);
  const twins = all.some((other) => other.id !== bot.id && botDisplayName(other, t) === name);
  return twins && team ? `${name} · ${team}` : name;
};

/** botLabel for components: compares with the viewer's own bots. */
export const useBotLabel = () => {
  const { t } = useTranslation();
  const { data } = useMyBotsQuery();
  const all = data?.bots;
  return useCallback((bot) => botLabel(bot, t, all || []), [t, all]);
};

/**
 * Where a chat in no project has its computer: the person's own place, per payer (a team
 * bot's in that team). The same id as backend agents/places.py home_id; the computer's
 * routes (/projects/{id}/workspace) take it in place of a project id.
 */
export const homePlaceId = (bot, userId) => {
  if (!bot || !userId) return null;
  return bot.owner_type === 'team' ? `home.${bot.owner_id}.${userId}` : `home.${userId}`;
};

export const botDisplayRole = (bot, t) =>
  bot?.description || (bot?.name_is_default ? t('bots.default_bot_role') : '');

/** Bots that can take work in a scope: a team's bots and, on team work, the viewer's
 * own bots too (they work for their owner in every team project); else a user's own. */
export const useScopeBots = (teamId, ownerId) => {
  const { data } = useMyBotsQuery();
  const { user } = useAuth();
  const all = data?.bots || [];
  return all.filter((b) => (teamId
    ? (b.owner_type === 'team' && b.owner_id === teamId) || (b.owner_type === 'user' && b.owner_id === user?.id)
    : b.owner_type === 'user' && b.owner_id === ownerId));
};
